import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Building2, Search, Users2, Layers, User as UserIcon } from 'lucide-react';
import AdminHeader from './AdminHeader';
import { crmCompanies, crmCompanyStats, hideCompany, restoreCompany, deleteCompany, companiesBulkHide, companiesBulkRestore, companiesBulkAssign, crmTeam } from '../lib/marketingApi';
import { useAdminAuth } from '../context/AdminAuthContext';
import { ViewTabs, ModerationMenu, RowCheck, BulkBar } from './ModerationControls';

function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-US', { dateStyle: 'medium' }); } catch (e) { return iso; }
}

export default function CompaniesCRM() {
  const navigate = useNavigate();
  const { user } = useAdminAuth();
  const isAdmin = user?.role === 'admin';
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [segment, setSegment] = useState('');
  const [search, setSearch] = useState('');
  const [view, setView] = useState('active');

  const loadStats = useCallback(() => { crmCompanyStats().then(setStats).catch(() => {}); }, []);
  useEffect(() => { loadStats(); }, [loadStats]);

  const load = useCallback(async () => {
    setItems(null);
    try {
      const d = await crmCompanies({ segment: segment || undefined, search: search || undefined, view, limit: 200 });
      setItems(d.items || []); setTotal(d.total || 0);
    } catch (e) { toast.error('Failed to load companies'); setItems([]); }
  }, [segment, search, view]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const reload = () => { load(); loadStats(); };
  const doHide = async (id, reason) => { try { await hideCompany(id, reason); toast.success(reason === 'spam' ? 'Marked as spam' : 'Moved to trash'); reload(); } catch (e) { toast.error('Action failed'); } };
  const doRestore = async (id) => { try { await restoreCompany(id); toast.success('Restored'); reload(); } catch (e) { toast.error('Restore failed'); } };
  const doPurge = async (id) => { if (!window.confirm('Permanently delete this company? This cannot be undone.')) return; try { await deleteCompany(id); toast.success('Permanently deleted'); reload(); } catch (e) { toast.error('Delete failed'); } };

  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [team, setTeam] = useState([]);
  useEffect(() => { crmTeam().then((r) => setTeam(r.members || [])).catch(() => {}); }, []);
  useEffect(() => { setSelected(new Set()); }, [view, segment, search]);
  const rows = items || [];
  const toggle = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allSelected = rows.length > 0 && rows.every((c) => selected.has(c.id));
  const toggleAll = () => setSelected(() => (allSelected ? new Set() : new Set(rows.map((c) => c.id))));
  const doBulk = async (reason) => {
    setBulkBusy(true);
    try { const r = await companiesBulkHide([...selected], reason); toast.success(`${r.hidden} moved to ${reason === 'spam' ? 'Spam' : 'Trash'}`); setSelected(new Set()); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Bulk action failed'); }
    finally { setBulkBusy(false); }
  };
  const doBulkRestore = async () => {
    setBulkBusy(true);
    try { const r = await companiesBulkRestore([...selected]); toast.success(`${r.restored} restored`); setSelected(new Set()); reload(); }
    catch (e) { toast.error('Restore failed'); }
    finally { setBulkBusy(false); }
  };
  const doBulkAssign = async (owner) => {
    if (!owner) return;
    setBulkBusy(true);
    try { const r = await companiesBulkAssign([...selected], owner); toast.success(`${r.assigned} assigned to ${owner}`); setSelected(new Set()); reload(); }
    catch (e) { toast.error('Assign failed'); }
    finally { setBulkBusy(false); }
  };

  const sel = 'h-9 rounded-md border border-slate-300 bg-white px-2.5 text-sm text-slate-700 outline-none focus:border-[#0035A8]';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="companies-crm">
      <AdminHeader active="companies" />
      <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8">
        <div className="mb-4 flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0035A8]/10 text-[#0035A8]"><Building2 className="h-4.5 w-4.5" /></span>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">Companies</h1>
            <p className="text-sm text-slate-500">Contacts grouped by company — enrich profiles and see everyone who's engaged</p>
          </div>
        </div>

        {stats && (
          <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="co-stat-total">
              <div className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-slate-400"><Building2 className="h-3.5 w-3.5" /> Companies</div>
              <div className="mt-1 text-2xl font-bold text-slate-900">{(stats.total || 0).toLocaleString()}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="co-stat-contacts">
              <div className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-slate-400"><Users2 className="h-3.5 w-3.5" /> Contacts in companies</div>
              <div className="mt-1 text-2xl font-bold text-sky-600">{(stats.contacts_in_companies || 0).toLocaleString()}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4 sm:col-span-2">
              <div className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-slate-400"><Layers className="h-3.5 w-3.5" /> By segment</div>
              <div className="mt-1.5 flex flex-wrap gap-1">
                {(stats.by_segment || []).slice(0, 6).map((s) => (
                  <span key={s.segment} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">{s.segment} · {s.count}</span>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <ViewTabs view={view} setView={setView} counts={{ spam: stats?.hidden_spam, deleted: stats?.hidden_deleted }} testidPrefix="companies" />
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search company / domain / industry" data-testid="co-search"
              className="h-9 w-72 rounded-md border border-slate-300 bg-white pl-8 pr-3 text-sm outline-none focus:border-[#0035A8]" />
          </div>
          <select value={segment} onChange={(e) => setSegment(e.target.value)} className={sel} data-testid="co-filter-segment">
            <option value="">All segments</option>
            {(stats?.by_segment || []).filter((s) => s.segment !== 'unclassified').map((s) => <option key={s.segment} value={s.segment}>{s.segment}</option>)}
          </select>
          <span className="ml-auto text-sm text-slate-500">{total.toLocaleString()} companies</span>
        </div>

        <BulkBar count={selected.size} label="companies" busy={bulkBusy}
          {...(view === 'active' ? {
            onSpam: () => doBulk('spam'), onTrash: () => doBulk('deleted'),
            assignChildren: (
              <select defaultValue="" data-testid="companies-bulk-assign" onChange={(e) => { const v = e.target.value; e.target.value = ''; doBulkAssign(v); }}
                className="h-8 rounded-md border border-slate-300 px-2 text-xs text-slate-700 outline-none focus:border-[#0035A8]">
                <option value="">Assign owner…</option>
                {team.map((m) => <option key={m.email} value={m.name}>{m.name}</option>)}
              </select>
            ),
          } : { onRestore: doBulkRestore })}
          onClear={() => setSelected(new Set())} testidPrefix="companies-bulk" />

        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="hidden grid-cols-12 gap-3 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 md:grid">
            <div className="col-span-3 flex items-center gap-2">{<RowCheck checked={allSelected} onChange={toggleAll} testid="companies-select-all" ariaLabel="Select all" />} Company</div><div className="col-span-2">Segment</div>
            <div className="col-span-1">Industry</div><div className="col-span-2">Assigned to</div>
            <div className="col-span-1 text-center">Contacts</div><div className="col-span-3 text-right">Last contacted</div>
          </div>
          {items === null ? (
            <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-16 text-slate-400" data-testid="co-empty"><Building2 className="h-8 w-8" /><p className="text-sm">No companies match these filters.</p></div>
          ) : items.map((c) => (
            <div key={c.id} role="button" tabIndex={0} onClick={() => navigate(`/admin/companies/${c.id}`)} onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/admin/companies/${c.id}`); }} data-testid={`co-row-${c.id}`}
              className="grid w-full cursor-pointer grid-cols-1 gap-2 border-b border-slate-100 px-5 py-3 text-left transition-colors hover:bg-slate-50 md:grid-cols-12 md:items-center md:gap-3">
              <div className="col-span-3 min-w-0 flex items-center gap-2">
                {<RowCheck checked={selected.has(c.id)} onChange={() => toggle(c.id)} testid={`co-select-${c.id}`} />}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-slate-900">{c.name || '—'}</div>
                  <div className="truncate text-xs text-slate-400">{c.domain || c.key}</div>
                </div>
              </div>
              <div className="col-span-2 truncate text-sm text-slate-600">{c.segment ? <span className="rounded-full bg-[#0035A8]/10 px-2 py-0.5 text-xs font-medium text-[#0035A8]">{c.segment}</span> : <span className="text-slate-400">—</span>}</div>
              <div className="col-span-1 truncate text-sm text-slate-600">{c.industry || '—'}</div>
              <div className="col-span-2 min-w-0 truncate text-sm">
                {c.owner ? <span className="inline-flex items-center gap-1 text-slate-700"><UserIcon className="h-3 w-3 text-slate-400" />{c.owner}</span> : <span className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">Unassigned</span>}
              </div>
              <div className="col-span-1 text-center text-sm font-semibold text-slate-700">{c.contact_count || 0}</div>
              <div className="col-span-3 flex items-center justify-end gap-1 text-xs text-slate-500">
                <span className="hidden sm:inline">{c.last_seen ? fmt(c.last_seen) : <span className="text-slate-300">—</span>}</span>
                <ModerationMenu view={view} isAdmin={isAdmin} testid={`company-${c.id}`} onSpam={() => doHide(c.id, 'spam')} onDelete={() => doHide(c.id, 'deleted')} onRestore={() => doRestore(c.id)} onPurge={() => doPurge(c.id)} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
